/**
 * MemberNode — the satellite that stands for a MEMBER AGENT this agent
 * consumes (`uses`) or starts (`starts`), or for an MCP server an operator
 * attached to it by hand.
 *
 * It is deliberately the SAME DRAWING as ModelNode: a small round-square icon
 * with a caption underneath, joined to whatever consumes or starts it by the
 * same dashed connector. Founder ruling (2026-08-25) on the two captioned
 * "capability bands" this replaced:
 *
 *   "don't invent new styles just use the icon of the agent … do what n8n did
 *    just like our model … a line attached to the agent that uses it, if in
 *    parent then rollup to the parent card"
 *
 * …and, the same day, on the full node BOX a started member used to get:
 *   "change this to this" — this tile, for every member, whichever word is on
 * its connector. So there is ONE vocabulary on this canvas — connector + icon
 * + caption — and a member is another thing hanging off a node, because that
 * is what it is. Nothing here is a chip, a band, or a badge.
 *
 * THE ICON IS THE MEMBER'S OWN. A deployed agent row carries `iconUrl`; when it
 * has none (or the file 404s, or the entry is an MCP server and not an agent at
 * all) it falls back to the letter tile every icon-less agent on this app
 * already uses. No invented glyph.
 *
 * SHAPE: a rounded SQUARE, not the model disc's circle, and that is not a
 * second style — it is this app's ONE agent-icon shape. The model disc is a
 * circle because it holds a VENDOR MARK (an inline SVG glyph on a transparent
 * ground). A marketplace agent icon is the opposite asset: a 256px macOS-style
 * badge, opaque RGB, drawn as a rounded square, sometimes on a WHITE field that
 * a circular clip leaves showing as white corners.
 *
 * RUNNING. When the member agent has runs in flight (`activeRuns` = running +
 * queued, the same number the Agents list's Running pill reads) the caption
 * glows in the app's running colour and a STACK OF PAPER rises behind the tile
 * — one sheet per run, each offset up-right behind the previous, so "how many
 * copies of this agent are working" is read at a glance ("if 2 then 2 layers N
 * layer"). Sheets are capped at MAX_SHEETS; past that a small "+N" badge at the
 * tile's top-right carries the remainder. Not running → plain tile, no sheets,
 * no glow. The amber `is-unset` ("still needs setting up") is a different
 * statement, kept on the tile's border, and can be true at the same time.
 *
 * Purely presentational — everything comes from `data`.
 */
import React from 'react';
import { Handle, Position } from 'reactflow';
import './MemberNode.css';

// The most sheets drawn behind the tile. Past this the stack stops growing
// (each sheet is only a 4px step, and five already read as "many") and the
// overflow badge names the rest.
export const MAX_SHEETS = 5;

/**
 * How the running state is drawn for `activeRuns` runs in flight.
 * @param {unknown} activeRuns
 * @returns {{running: boolean, sheets: number, overflow: number}}
 */
export function runningStack(activeRuns) {
  const n = Math.max(0, Math.floor(Number(activeRuns) || 0));
  return {
    running: n > 0,
    sheets: Math.min(n, MAX_SHEETS),
    overflow: Math.max(0, n - MAX_SHEETS),
  };
}

export const MemberNode = ({ data }) => {
  const {
    name = '', iconUrl = '', notReady = false, onNodeClick, tooltip, attached = true,
    activeRuns = 0, compact = false, qualifier = '',
  } = data || {};
  const letter = (String(name).replace(/[^A-Za-z0-9]/g, '').charAt(0) || '?').toUpperCase();
  const go = () => { if (onNodeClick) onNodeClick(); };
  const { running, sheets, overflow } = runningStack(activeRuns);

  return (
    <div
      className={[
        'gwe-member-node',
        notReady && 'is-unset',
        running && 'is-running',
        compact && 'is-compact',
      ].filter(Boolean).join(' ')}
      title={tooltip || undefined}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); go(); }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault(); e.stopPropagation(); go();
      }}
    >
      {/* Only rendered when something actually connects to it. An AGENT-level
          member has no connector at all (see memberSatellites), and a handle
          with no edge is an anchor for a line that does not exist. */}
      {attached && (
        <Handle
          type="target"
          position={Position.Left}
          id="disc"
          isConnectable={false}
          className="gwe-member-node__handle"
        />
      )}
      {/* The paper stack: sheet 1 sits directly behind the tile, each further
          sheet one step up-right behind the previous. Rendered before the tile
          so the tile paints over them without z-index games. */}
      {Array.from({ length: sheets }, (_, i) => (
        <div
          key={i}
          className="gwe-member-node__sheet"
          style={{ '--sheet': i + 1 }}
          aria-hidden
        />
      ))}
      <div className="gwe-member-node__tile">
        {iconUrl
          ? <img src={iconUrl} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          : <span className="gwe-member-node__letter">{letter}</span>}
      </div>
      {overflow > 0 && (
        <div className="gwe-member-node__overflow" aria-label={`${overflow} more runs`}>
          +{overflow}
        </div>
      )}
      <div className="gwe-member-node__caption">
        <div className="gwe-member-node__label">{name}</div>
        {/* One line of prose under the name ("inherited · limited") — the mark
            that tells an inherited link from an attached one on the canvas. */}
        {qualifier && <div className="gwe-member-node__qualifier">{qualifier}</div>}
      </div>
    </div>
  );
};

export default MemberNode;
